import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  ReactNode,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Placement = "top" | "bottom" | "left" | "right";

export interface TourStep {
  id: string;
  title: string;
  description: ReactNode;
  selector: string;
  placement?: Placement;
}

export interface TourOptions {
  onFinish?: () => void;
  onExit?: () => void;
  maskPadding?: number;
}

interface TourState {
  steps: TourStep[];
  currentIndex: number;
  isActive: boolean;
  options?: TourOptions;
}

interface TourContextValue {
  startTour: (steps: TourStep[], options?: TourOptions) => void;
  next: () => void;
  back: () => void;
  exit: () => void;
  isActive: boolean;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

// Fallback to a safe no-op implementation to avoid crashing when the provider
// is not mounted (e.g., during SSR/render errors). This preserves UX instead
// of white-screening the app.
export const useTour = (): TourContextValue => {
  const ctx = useContext(TourContext);
  if (!ctx) {
    return {
      startTour: () => {},
      next: () => {},
      back: () => {},
      exit: () => {},
      isActive: false,
    };
  }
  return ctx;
};

export const TourProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<TourState>({
    steps: [],
    currentIndex: 0,
    isActive: false,
    options: undefined,
  });

  const startTour = useCallback((steps: TourStep[], options?: TourOptions) => {
    if (!steps.length) return;
    setState({
      steps,
      currentIndex: 0,
      isActive: true,
      options,
    });
  }, []);

  const exit = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: false,
    }));
    state.options?.onExit?.();
  }, [state.options]);

  const next = useCallback(() => {
    setState(prev => {
      const nextIndex = prev.currentIndex + 1;
      if (nextIndex >= prev.steps.length) {
        prev.options?.onFinish?.();
        return { ...prev, isActive: false };
      }
      return { ...prev, currentIndex: nextIndex };
    });
  }, []);

  const back = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentIndex: Math.max(0, prev.currentIndex - 1),
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      startTour,
      next,
      back,
      exit,
      isActive: state.isActive,
    }),
    [startTour, next, back, exit, state.isActive],
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      <TourOverlay state={state} onNext={next} onBack={back} onExit={exit} />
    </TourContext.Provider>
  );
};

interface TourOverlayProps {
  state: TourState;
  onNext: () => void;
  onBack: () => void;
  onExit: () => void;
}

const TourOverlay: React.FC<TourOverlayProps> = ({ state, onNext, onBack, onExit }) => {
  const { steps, currentIndex, isActive, options } = state;
  const currentStep = steps[currentIndex];
  const maskPadding = options?.maskPadding ?? 8;
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      return;
    }

    const updateRect = () => {
      const el = document.querySelector(currentStep.selector) as HTMLElement | null;
      if (!el) {
        setTargetRect(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [isActive, currentStep]);

  useEffect(() => {
    if (!isActive) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onExit();
      } else if (event.key === "ArrowRight" || event.key === "Enter") {
        onNext();
      } else if (event.key === "ArrowLeft") {
        onBack();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isActive, onNext, onBack, onExit]);

  useEffect(() => {
    if (!isActive || !currentStep) return;
    if (currentStep.id === 'preview') return; // avoid scrolling preview step to mid-page
    const el = document.querySelector(currentStep.selector) as HTMLElement | null;
    if (!el) return;

    const isInView = () => {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    };

    if (!isInView()) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }, [isActive, currentStep]);

  const cardPosition = useMemo(() => {
    if (!targetRect) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }

    const cardGap = maskPadding + 12;
    const base = { top: targetRect.bottom + cardGap, left: targetRect.left };

    switch (currentStep?.placement) {
      case "top":
        return {
          top: targetRect.top - cardGap,
          left: targetRect.left,
          transform: "translateY(-100%)",
        };
      case "left":
        return {
          top: targetRect.top,
          left: targetRect.left - cardGap,
          transform: "translateX(-100%)",
        };
      case "right":
        return {
          top: targetRect.top,
          left: targetRect.right + cardGap,
        };
      case "bottom":
      default:
        return base;
    }
  }, [currentStep?.placement, maskPadding, targetRect]);

  const arrowPosition = useMemo(() => {
    if (!targetRect) return undefined;
    const size = 12;
    switch (currentStep?.placement) {
      case "top":
        return {
          top: targetRect.top - (maskPadding + 8),
          left: targetRect.left + targetRect.width / 2 - size / 2,
          rotate: "45deg",
          origin: "center",
        };
      case "left":
        return {
          top: targetRect.top + targetRect.height / 2 - size / 2,
          left: targetRect.left - (maskPadding + 8),
          rotate: "45deg",
          origin: "center",
        };
      case "right":
        return {
          top: targetRect.top + targetRect.height / 2 - size / 2,
          left: targetRect.right + (maskPadding + 8),
          rotate: "45deg",
          origin: "center",
        };
      case "bottom":
      default:
        return {
          top: targetRect.bottom + (maskPadding + 8),
          left: targetRect.left + targetRect.width / 2 - size / 2,
          rotate: "45deg",
          origin: "center",
        };
    }
  }, [currentStep?.placement, maskPadding, targetRect]);

  if (!isActive || !currentStep) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      aria-live="polite"
      aria-label="Guided tutorial"
    >
      <div className="absolute inset-0" onClick={onExit} />

      {targetRect ? (
        <div
          className="pointer-events-none fixed border border-primary/70 transition-all"
          style={{
            top: targetRect.top - maskPadding,
            left: targetRect.left - maskPadding,
            width: targetRect.width + maskPadding * 2,
            height: targetRect.height + maskPadding * 2,
            borderRadius: 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/60" />
      )}

      <div
        className={cn(
          "fixed w-[320px] max-w-[90vw] rounded-xl border shadow-2xl",
          "p-4 space-y-3 animate-in fade-in zoom-in",
          "relative overflow-hidden bg-white border-[#5748C6]/50"
        )}
        style={cardPosition}
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#5748C6]/15 via-transparent to-transparent" />

        <div className="flex items-center justify-between text-xs font-semibold text-primary/90 uppercase tracking-wide">
          <span>Guided tour</span>
          <span className="text-foreground/70">Step {currentIndex + 1} of {steps.length}</span>
        </div>
        <div className="space-y-1">
          <h3 className="text-lg font-bold text-foreground">{currentStep.title}</h3>
          <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
            {currentStep.description}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onExit}>
            Skip
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onBack} disabled={currentIndex === 0}>
              Back
            </Button>
            <Button size="sm" onClick={onNext}>
              {currentIndex === steps.length - 1 ? "Finish" : "Next"}
            </Button>
          </div>
        </div>
      </div>

      {arrowPosition && (
        <div
          className="fixed w-3 h-3 bg-card border border-border rotate-45"
          style={{
            top: arrowPosition.top,
            left: arrowPosition.left,
          }}
        />
      )}
    </div>,
    document.body
  );
};
